/**
 * Hook personnalisé pour l'auto-refresh périodique
 *
 * Ce hook gère :
 * - Rafraîchissement automatique toutes les 5 minutes
 * - Pause du rafraîchissement quand l'onglet est inactif (Page Visibility API)
 * - Formatage du temps écoulé depuis le dernier refresh
 * - Rafraîchissement manuel
 *
 * @param {Function} onRefresh - Callback appelé lors du rafraîchissement
 * @param {number} intervalMs - Intervalle en millisecondes (défaut: 5 minutes)
 * @param {Object} options - refreshOnVisible contrôle le rechargement au retour sur la page
 * @returns {Object} { lastRefreshTime, refreshNow, formatLastRefreshTime }
 */

import { useState, useEffect, useCallback, useRef } from 'react';

const DEFAULT_INTERVAL = 5 * 60 * 1000; // 5 minutes en millisecondes

export const useAutoRefresh = (onRefresh, intervalMs = DEFAULT_INTERVAL, { refreshOnVisible = true } = {}) => {
  const [lastRefreshTime, setLastRefreshTime] = useState(Date.now());
  const [countdown, setCountdown] = useState(Math.floor(intervalMs / 1000));
  const [isVisible, setIsVisible] = useState(!document.hidden);
  const intervalRef = useRef(null);
  const countdownRef = useRef(null);
  const nextRefreshAt = useRef(Date.now() + intervalMs);
  const onRefreshRef = useRef(onRefresh);

  // Mettre à jour la ref à chaque render pour éviter les dépendances obsolètes
  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  /**
   * Fonction pour rafraîchir immédiatement
   */
  const refreshNow = useCallback(() => {
    if (onRefreshRef.current) {
      onRefreshRef.current();
      const now = Date.now();
      setLastRefreshTime(now);
      nextRefreshAt.current = now + intervalMs;
      setCountdown(Math.floor(intervalMs / 1000));
    }
  }, [intervalMs]);

  /**
   * Formatte le temps écoulé depuis le dernier refresh
   * @returns {string} Temps formaté (ex: "il y a 2 min")
   */
  const formatLastRefreshTime = useCallback(() => {
    const now = Date.now();
    const diffMs = now - lastRefreshTime;
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);

    if (diffSeconds < 10) {
      return "à l'instant";
    } else if (diffSeconds < 60) {
      return `il y a ${diffSeconds}s`;
    } else if (diffMinutes < 60) {
      return `il y a ${diffMinutes} min`;
    } else if (diffHours === 1) {
      return "il y a 1 heure";
    } else {
      return `il y a ${diffHours} heures`;
    }
  }, [lastRefreshTime]);

  // Gérer la visibilité de la page avec Page Visibility API
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsVisible(!document.hidden);

      // Le retour sur l’accueil reprend le minuteur sans recharger les données.
      if (!document.hidden) {
        if (refreshOnVisible) refreshNow();
        else {
          nextRefreshAt.current = Date.now() + intervalMs;
          setCountdown(Math.floor(intervalMs / 1000));
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refreshNow, refreshOnVisible, intervalMs]);

  // Gérer l'auto-refresh avec intervalle
  useEffect(() => {
    // Ne rafraîchir que si la page est visible
    if (!isVisible) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Configurer l'intervalle de rafraîchissement
    intervalRef.current = setInterval(() => {
      refreshNow();
    }, intervalMs);

    // Nettoyer l'intervalle au démontage ou changement
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isVisible, intervalMs, refreshNow]);

  // Countdown seconde par seconde
  useEffect(() => {
    countdownRef.current = setInterval(() => {
      const secs = Math.max(0, Math.round((nextRefreshAt.current - Date.now()) / 1000));
      setCountdown(secs);
    }, 1000);
    return () => clearInterval(countdownRef.current);
  }, []);

  return {
    lastRefreshTime,
    countdown,
    refreshNow,
    formatLastRefreshTime,
  };
};

export default useAutoRefresh;
